import { ifAny, IFieldDef, IGetValueArgs } from "@components/smart/FieldInfo";
import { isSystemEntity } from "@components/smart/GeneralFieldDefinition";
import { createPath } from "@odata/BindingContext";
import {
    AccountEntity,
    ChartOfAccountsTemplateEntity,
    EntitySetName,
    EntityTypeName,
    UserEntity
} from "@odata/GeneratedEntityTypes";
import { AccountingCode } from "@odata/GeneratedEnums";
import { cloneDeep } from "lodash";

import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { Sort } from "../../enums";
import { getDefinitions as chartOfAccountsDefinitions } from "../chartOfAccounts/ChartOfAccountsDef";
import { setDefByEntityType } from "../getDefByEntityType";
import { IDefinition, IGetDefinition } from "../PageUtils";
import ChartOfAccountsTemplatesFormView from "./ChartOfAccountsTemplatesFormView";

export const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {
    const definition = cloneDeep(chartOfAccountsDefinitions(context));

    definition.table.id = `${EntityTypeName.ChartOfAccountsTemplate}Table`;
    definition.form.id = `${EntityTypeName.ChartOfAccountsTemplate}Form`;
    definition.form.formControl = ChartOfAccountsTemplatesFormView;
    definition.form.translationFiles = getDefinitions.translationFiles;

    // const removeProps = Object.values(AccountEntity).filter((prop: string) => !Object.values(AccountTemplateEntity).includes(prop as unknown as AccountTemplateEntity)) as string[];
    // todo: hardcoded for now, because template entity has IsActive for some reason
    const removeProps = [AccountEntity.IsClosed, AccountEntity.IsActive] as string[];

    for (const prop of removeProps) {
        delete definition.table.filterBarDef[0].filterDefinition[prop];
        delete definition.table.columnDefinition[prop];
        delete definition.form.fieldDefinition[prop];
    }
    definition.table.columns = definition.table.columns.filter(item => !removeProps.includes(item));
    definition.table.filterBarDef[0].defaultFilters = definition.table.filterBarDef[0].defaultFilters.filter(item => !removeProps.includes(item));

    // We do not show title for chart of account templates, it's editable.
    definition.table.title = null;

    definition.table.parentDefinition = {
        sort: [{ id: ChartOfAccountsTemplateEntity.Name, sort: Sort.Asc }],
        columns: [
            { id: ChartOfAccountsTemplateEntity.Name },
            { id: ChartOfAccountsTemplateEntity.DateCreated },
            { id: ChartOfAccountsTemplateEntity.DateLastModified },
            { id: ChartOfAccountsTemplateEntity.AccountingCode },
            { id: createPath(ChartOfAccountsTemplateEntity.CreatedBy, UserEntity.Name) },
            { id: ChartOfAccountsTemplateEntity.LastModifiedBy },
            { id: ChartOfAccountsTemplateEntity.Accounting }
        ],
        entitySet: EntitySetName.ChartOfAccountsTemplates,
        filter: (_: IGetValueArgs) => {
            let filter = context.getAddingNewCompany() ? null : `${ChartOfAccountsTemplateEntity.Company} eq null`;
            return filter;
        }
    };

    for (const group of definition.form.groups) {
        group.rows.forEach((row, idx) => {
            group.rows[idx] = row.filter(item => !removeProps.includes((item as IFieldDef).id));
        });
        // filter out empty rows
        group.rows = group.rows.filter(row => row.length);
    }

    const _baseReadOnly = definition.form.isReadOnly;
    definition.form.isReadOnly = ifAny(_baseReadOnly, isSystemEntity);
    
    definition.entitySet = (args: IGetValueArgs) => {
        let activeCOAId = args.data?.parentId;

        if (!activeCOAId) {
            // TODO how to show empty table without fake binding context
            activeCOAId = -1000;
        }

        return `${EntitySetName.ChartOfAccountsTemplates}(${activeCOAId})/Accounts`;
    };

    return definition;
};

getDefinitions.translationFiles = ["ChartsOfAccountsTemplates", ...chartOfAccountsDefinitions.translationFiles];
setDefByEntityType(EntityTypeName.AccountTemplate, getDefinitions);